import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Box, Card, CardContent, Button, Typography, CircularProgress, Avatar } from '@mui/material';
import {
  CheckCircle as CheckIcon,
  ReplayCircleFilled as UsedIcon,
  HourglassDisabled as ExpiredIcon,
  ErrorOutline as InvalidIcon,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Visual + copy for each activation outcome returned by the backend.
const VIEWS = {
  activated: {
    color: 'success.main',
    Icon: CheckIcon,
    title: 'Account verified',
    body: 'Your email is verified and your account is active. You can now sign in to the provider portal.',
  },
  already_used: {
    color: 'primary.main',
    Icon: UsedIcon,
    title: 'Link already used',
    body: 'This activation link has already been used — your account is already verified. Please sign in.',
  },
  expired: {
    color: 'warning.main',
    Icon: ExpiredIcon,
    title: 'Link expired',
    body: 'Activation links are valid for 48 hours and this one has expired. Ask your clinic administrator to re-send your welcome email for a fresh link.',
  },
  invalid: {
    color: 'error.main',
    Icon: InvalidIcon,
    title: 'Invalid link',
    body: 'This activation link is not valid. Make sure you opened the most recent link from your welcome email, or ask your clinic administrator to re-send it.',
  },
};

// Activation is one-time, and React runs this effect twice in development (StrictMode):
// the first call activated the account and the second reported "link already used",
// which is what the person saw. Both runs share one request instead.
const inflight = new Map();
function activateOnce(token) {
  if (!inflight.has(token)) {
    inflight.set(token, axios.post(`${API_URL}/api/v1/auth/provider/activate`, { token }));
  }
  return inflight.get(token);
}

export default function Activate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [status, setStatus] = useState(null); // null = loading

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setStatus('invalid');
      return undefined;
    }
    activateOnce(token)
      .then((res) => {
        if (!cancelled) setStatus(res.data?.status || 'invalid');
      })
      .catch(() => {
        if (!cancelled) setStatus('invalid');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (status === null) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const view = VIEWS[status] || VIEWS.invalid;
  const { Icon } = view;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        bgcolor: 'background.default',
      }}
    >
      <Card sx={{ maxWidth: 460, width: '100%', textAlign: 'center' }}>
        <CardContent sx={{ p: 4 }}>
          <Avatar sx={{ width: 64, height: 64, bgcolor: view.color, mx: 'auto', mb: 2 }}>
            <Icon sx={{ fontSize: 36 }} />
          </Avatar>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {view.title}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {view.body}
          </Typography>
          <Button variant="contained" size="large" fullWidth onClick={() => navigate('/login')}>
            Go to sign in
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}
